import { useContext, useState, useMemo, useEffect, useCallback } from 'react';

import { MenuFeaturesContext } from 'modules/menu/components/MenuFeaturesContext';
import { MenuPermissionsContext } from 'modules/menu/components/MenuPermissionsContext';

import { csvColumnsTranslationMap, identicationColumns } from '../constants';
import { CSVColumnKey } from '../types';

const allColumnKeys = Object.keys(csvColumnsTranslationMap) as CSVColumnKey[];

const useAvailableColumnKeys = (mode: 'download' | 'upload') => {
  const { isRetail, overTheCounter, hasPickerEnabled, supportWeightedItems, mandatoryWarnings, bottleDeposits } =
    useContext(MenuFeaturesContext);
  const { customCommissionEdit, customCommissionRead } = useContext(MenuPermissionsContext);

  const [availableCsvColumns, setAvailableCsvColumns] = useState<CSVColumnKey[]>([]);
  const [selectedColumns, setSelectedColumns] = useState<CSVColumnKey[]>([]);

  const columnAvailabilities = useMemo<Partial<Record<CSVColumnKey, boolean>>>(
    () => ({
      item_id: mode === 'download',
      is_over_the_counter: overTheCounter,
      use_inventory_management: isRetail,
      is_in_stock: isRetail,
      location: hasPickerEnabled && isRetail,
      commission_pickup: mode === 'download' ? customCommissionRead : customCommissionEdit,
      commission_delivery: mode === 'download' ? customCommissionRead : customCommissionEdit,
      mandatory_warnings: mandatoryWarnings,
      weighted_item_sell_by: supportWeightedItems,
      weighted_item_approx_weight_in_grams: supportWeightedItems,
      weighted_item_min_weight_in_grams: supportWeightedItems,
      deposit_value: bottleDeposits,
      deposit_vat: bottleDeposits,
    }),
    [
      bottleDeposits,
      customCommissionEdit,
      customCommissionRead,
      hasPickerEnabled,
      isRetail,
      mandatoryWarnings,
      mode,
      overTheCounter,
      supportWeightedItems,
    ],
  );

  const isAvailableColumn = useCallback(
    (column: CSVColumnKey) => availableCsvColumns.includes(column),
    [availableCsvColumns],
  );

  const initializeColumnsForUpload = (fields: CSVColumnKey[]) => {
    const newAvailableColumns = fields.filter(key => columnAvailabilities[key] ?? true);
    setAvailableCsvColumns(newAvailableColumns);
    setSelectedColumns(newAvailableColumns.filter(field => !identicationColumns.includes(field)));
  };

  const selectColumns = useCallback(
    (columns: CSVColumnKey[]) => {
      setSelectedColumns(previousColumns => [...new Set([...previousColumns, ...columns])].filter(isAvailableColumn));
    },
    [isAvailableColumn],
  );

  const deselectColumns = useCallback((columns: CSVColumnKey[]) => {
    setSelectedColumns(previousColumns => previousColumns.filter(column => !columns.includes(column)));
  }, []);

  useEffect(() => {
    setAvailableCsvColumns(mode === 'download' ? allColumnKeys.filter(key => columnAvailabilities[key] ?? true) : []);
    setSelectedColumns(mode === 'download' ? [...identicationColumns, 'name'] : []);
  }, [columnAvailabilities, mode]);

  return { availableCsvColumns, selectedColumns, initializeColumnsForUpload, selectColumns, deselectColumns };
};

export default useAvailableColumnKeys;
